import { Button } from '@chakra-ui/button'
import { Flex, Stack } from '@chakra-ui/react'
import type { InsuranceParamsPropertyDto, WizardPropertyAdditionalData } from 'BrokerTsLib/src/dtos'
import { Insurer, Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { prepareSmartTravelInsuredPersons } from 'BrokerTsLib/src/helpers/smartTravel'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { PolicyHolderPropertyStep4ValidationSchema, SmartTravelValidationSchema } from 'BrokerTsLib/src/validation'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import * as yup from 'yup'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useNameof } from '@/hooks/useNameof'
import { useSaveAdditionalData, useUpdateLastWizardStep } from '@/hooks/useWizard'
import { Step3Sections } from '@/pages/steps/property/3'

import { useSidebar } from '@/context/SidebarContext'
import type { PageSection as Section } from '@/layouts/components/Sidebar'
import { FormSection } from '../../../forms/FormSection'
import { AdditionalPolicyHolderInfoForm } from '../../property/Step3/forms/AdditionalPolicyHolderInfoForm'
import { AdditionalContactInfoForm } from './forms/AdditionalContactInfoForm'
import { AdditionalInfoForm } from './forms/AdditionalInfoForm'
import { AdditionalInsuredInfoForm } from './forms/AdditionalInsuredInfoForm'
import { SmartTravelInsuredPersonsForm } from './forms/SmartTravelInsuredPersonsForm'

export const schema = yup.object().shape(
  {
    policyHolder: PolicyHolderPropertyStep4ValidationSchema,
    property: yup.object().shape({
      additionalDataGcp: yup.object().shape({
        smartTravel: SmartTravelValidationSchema.nullable().default(null),
      }).nullable().default(null),
    }).nullable().default(null),
  }
)

export const Step3 = ({ data }: { data: WizardPropertyAdditionalData }) => {
  const navigate = useNavigate()
  const { insuranceParams, refetchInsuranceParams, insuranceParamsId: paramsId, isDisabled } = useInsuranceParams()
  const { mutateAsync: saveAdditionalInfo } = useSaveAdditionalData()
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()

  const nameof = useNameof<InsuranceParamsPropertyDto>()
  const personPrefix = nameof(x => x.policyHolder.person)

  // Chytré cestovko - předpřipravení řádků pojištěných osob (musí být součástí výchozích hodnot formuláře)
  prepareSmartTravelInsuredPersons(data)

  const formDefaultValues: WizardPropertyAdditionalData = {
    ...data,
    nextPage: 'next'
  }

  // const schema = yup.object().shape({}) as any

  const methods = useForm<WizardPropertyAdditionalData>({
    values: formDefaultValues,
    resolver: yupResolver(schema) as any, // aby se pripadne resetovaly serverove validace
    mode: 'onBlur',
  })

  // v nevalidniho formulare se nascroluje na validationSummary
  const validationSummaryRef = useRef<HTMLDivElement>(null)
  const scrollToValidationSummary = useCallback(() => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }, [validationSummaryRef])

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    setError,
    getValues,
    reset,
    watch
  } = methods

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.PropertyInsurance, 3, paramsId, methods)

  const submitForm = handleSubmit((data) => {
    const values = cloneDeep(data)

    // datum narození posílám jen jako datum bez času, jinak dochází k časovému posunu (na serveru předchozí den)
    values.property?.additionalDataGcp?.smartTravel?.insuredPersons?.forEach(person => {
      if (person.birthdate) person.birthdate = formatDate(person.birthdate as Date)
    })

    return saveAdditionalInfo(values)
      .then((response) => {
        reset(data)
        insuranceParamsFormPersistance.clear()

        if (values.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 3) == 3) {
            updateStep({ paramsId: Number(paramsId), step: 4 })
              .then(() => refetchInsuranceParams())
          }

          // aby se stihl resetovat formular a smazal local storage pred navigaci (jinak se zobrazuje okno s neulozenymi daty)
          setTimeout(() => {
            navigate(`/property/steps/4/${paramsId}`)
          }, 0)
        }
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })

        scrollToValidationSummary()
      })
      .finally(() => { })
  }, (err) => {
    scrollToValidationSummary()
  })

  // registrace sekcí
  const { pageSections, setPageSections } = useSidebar()

  // GCP sekce předmětu pojištění se zobrazuje jen pokud je opravdu co vyplnit
  // (samotné chytré cestovko nastavuje additionalDataRequired, ale zobrazuje se ve vlastní sekci)
  const additionalDataGcp = data?.property?.additionalDataGcp
  const showAdditionalInfoSection = !!data?.property?.additionalDataRequired &&
    (data.insurerId != Insurer.GeneraliCeskaPojistovna ||
      !!(additionalDataGcp && (additionalDataGcp.notOwnerPropertyLossRequired ||
        additionalDataGcp.notOwnerPropertyResponsibilityLossRequired ||
        additionalDataGcp.startConstructionDateRequired)))

  useEffect(() => {
    const array: Section[] = []
    let i = 1

    if (showAdditionalInfoSection) {
      array.push({
        id: Step3Sections.additionalInfo,
        sectionTitle: 'Doplňující informace o předmětu pojištění',
        index: i,
        children: (
          <CustomWaypoint sectionId={`section_${Step3Sections.additionalInfo}`}>
            <PageSection id={`section_${Step3Sections.additionalInfo}`}>
              <FormSection number={i++} sectionTitle="Doplňující informace o předmětu pojištění">
                <AdditionalInfoForm data={data} />
              </FormSection>
            </PageSection>
          </CustomWaypoint>
        ),
      })
    }

    array.push({
      id: Step3Sections.pojistnik,
      sectionTitle: 'Pojistník',
      index: i,
      children: (
        <CustomWaypoint sectionId={`section_${Step3Sections.pojistnik}`}>
          <PageSection id={`section_${Step3Sections.pojistnik}`}>
            <FormSection number={i++} sectionTitle="Pojistník">
              <AdditionalPolicyHolderInfoForm personPrefix={personPrefix} />
            </FormSection>
          </PageSection>
        </CustomWaypoint>
      ),
    })

    array.push({
      id: Step3Sections.pojisteny,
      sectionTitle: 'Pojištěný',
      index: i,
      children: (
        <CustomWaypoint sectionId={`section_${Step3Sections.pojisteny}`}>
          <PageSection id={`section_${Step3Sections.pojisteny}`} >
            <FormSection number={i++} sectionTitle="Pojištěný">
              <AdditionalInsuredInfoForm />
            </FormSection>
          </PageSection>
        </CustomWaypoint >
      ),
    })

    if (data?.insurerId == Insurer.GeneraliCeskaPojistovna && data?.property?.additionalDataGcp?.smartTravel) {
      array.push({
        id: Step3Sections.smartTravel,
        sectionTitle: 'Chytré cestovko – pojištěné osoby',
        index: i,
        children: (
          <CustomWaypoint sectionId={`section_${Step3Sections.smartTravel}`}>
            <PageSection id={`section_${Step3Sections.smartTravel}`} >
              <FormSection number={i++} sectionTitle="Chytré cestovko – pojištěné osoby">
                <SmartTravelInsuredPersonsForm />
              </FormSection>
            </PageSection>
          </CustomWaypoint >
        ),
      })
    }

    array.push({
      id: Step3Sections.address,
      sectionTitle: 'Korespondenční adresa',
      index: i,
      children: (
        <CustomWaypoint sectionId={`section_${Step3Sections.address}`}>
          <PageSection id={`section_${Step3Sections.address}`} >
            <FormSection number={i++} sectionTitle="Korespondenční adresa">
              <AdditionalContactInfoForm />
            </FormSection>
          </PageSection>
        </CustomWaypoint >
      ),
    })

    setPageSections(array)
  }, [data])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Stack gap={8}>
          {pageSections.map(x => (
            <CustomWaypoint key={x.id} sectionId={x.id}>
              <PageSection id={x.id}>
                {x.children}
              </PageSection>
            </CustomWaypoint>
          ))}

          {!isDisabled &&
            < Flex justifyContent='center'>
              <Button
                type="button"
                variant="solid"
                colorScheme="green"
                isLoading={isSubmitting}
                onClick={submitForm}
              >
                Pokračovat
              </Button>
            </Flex>
          }

          <ErrorSummary errors={errors} ref={validationSummaryRef} />
          <UnsavedFormModal {...insuranceParamsFormPersistance} />
        </Stack>
      </form >
    </FormProvider >
  )
}
